import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";

const maskCPF = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  let out = d.slice(0, 3);
  if (d.length > 3) out += `.${d.slice(3, 6)}`;
  if (d.length > 6) out += `.${d.slice(6, 9)}`;
  if (d.length > 9) out += `-${d.slice(9)}`;
  return out;
};

export default function ExcluirContaPage() {
  const [params] = useSearchParams();
  const [cpf, setCpf] = useState(() => maskCPF(params.get("cpf") || ""));
  const [confirma, setConfirma] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState(false);
  const origem = params.get("origem") || "";

  const cpfDigits = cpf.replace(/\D/g, "");
  const valido = cpfDigits.length === 11;

  const enviar = async () => {
    if (!valido || !confirma || enviando) return;
    setEnviando(true);
    try {
      const r = await fetch(`${API}/public/excluir-conta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cpf: cpfDigits, origem: origem || undefined }),
      });
      const d = await r.json();
      if (d.ok) setFeito(true);
      else toast.error("Não foi possível concluir agora. Tente novamente.");
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setEnviando(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: "#11131b", fontFamily: "'Hanken Grotesk', 'Inter', sans-serif" }}
      data-testid="excluir-conta-page">
      <div className="w-full max-w-md rounded-[14px] p-6"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        {feito ? (
          <div className="text-center" data-testid="excluir-conta-sucesso">
            <CheckCircle2 className="h-12 w-12 mx-auto" style={{ color: "#7ef0a0" }} />
            <h1 className="mt-4 text-lg font-bold" style={{ color: "#e1e2ed" }}>Solicitação registrada</h1>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "#8d90a0" }}>
              Se o CPF informado estiver cadastrado, o acesso ao aplicativo foi revogado
              e os dados serão excluídos conforme nossa política de privacidade.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-7 w-7 shrink-0" style={{ color: "#ffb4ab" }} />
              <h1 className="text-lg font-bold" style={{ color: "#e1e2ed" }}>Excluir minha Conta</h1>
            </div>
            <div className="mt-4 rounded-xl p-3"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)" }}
              data-testid="excluir-conta-alerta">
              <p className="text-xs leading-relaxed" style={{ color: "#ffb4ab" }}>
                Atenção: seu cadastro será <b>revogado</b> e seus dados <b>excluídos</b>,
                impedindo o acesso ao aplicativo. Esta ação é definitiva.
              </p>
            </div>
            <label className="block mt-5 text-xs font-medium" style={{ color: "#8d90a0" }}
              htmlFor="excluir-cpf">CPF do titular da conta *</label>
            <input id="excluir-cpf" data-testid="excluir-conta-cpf"
              className="mt-1.5 w-full rounded-xl px-3.5 py-3 text-sm outline-none"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#e1e2ed" }}
              placeholder="000.000.000-00" inputMode="numeric"
              value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} />
            <label className="flex items-start gap-2.5 mt-4 cursor-pointer" data-testid="excluir-conta-confirma-label">
              <input type="checkbox" data-testid="excluir-conta-confirma"
                className="mt-0.5 h-4 w-4 accent-red-500"
                checked={confirma} onChange={(e) => setConfirma(e.target.checked)} />
              <span className="text-xs leading-relaxed" style={{ color: "#c3c6d7" }}>
                Confirmo definitivamente a exclusão da minha conta e dos meus dados.
              </span>
            </label>
            <button type="button" data-testid="excluir-conta-submit"
              onClick={enviar} disabled={!valido || !confirma || enviando}
              className="mt-5 w-full rounded-xl py-3.5 text-sm font-semibold text-white transition-opacity"
              style={{ background: "#dc2626", opacity: (!valido || !confirma || enviando) ? 0.45 : 1 }}>
              {enviando ? "Excluindo..." : "Excluir definitivamente"}
            </button>
            <p className="mt-4 text-center text-[11px]" style={{ color: "#8d90a0" }}>
              Dúvidas? Consulte nossa{" "}
              <a href="https://app.braveaccess.com.br/politica-privacidade.html" target="_blank"
                rel="noopener noreferrer" style={{ color: "#8ab4ff" }}
                data-testid="excluir-conta-politica">Política de Privacidade</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
